import React from 'react'
import * as R from 'ramda'
import { Accordion, Form, Input } from 'semantic-ui-react'

import VisibilityPermissionForm from './components/VisibilityPermissionForm'
import ActionPermissionForm from './components/ActionPermissionForm'
import ActionGroupForm from './components/ActionGroupForm'
import { Message } from 'semantic-ui-react'
import ErrorText from 'shared/components/forms/ErrorText'

export default function CustomRoleForm({
  hiddenRefButton,
  onChange,
  state,
  onSubmit,
  isFetchingSurveys,
  isFetchingDashboards,
  isDisabled,
  isDefaultRole,
  isDefaultCollaborator,
  loading,
  nameError,
}: CustomRoleFormProps): JSX.Element {
  const permissions: Permission[] = [
    {
      title: 'Surveys',
      view_permission: {
        name: 'view_surveys_page',
        value: state.permissions['view_surveys_page'],
        description:
          'Control access to surveys and related feedback. Permissions also impact Dashboards, Feedback, Customers, and Reporting.',
        options: [
          {
            key: 'all',
            text: 'All surveys',
            value: 'all',
          },
          {
            key: 'restricted_per_account',
            text: 'Assign surveys individually to users',
            value: 'restricted_per_account',
          },
          {
            key: 'restricted_per_role',
            text: 'Assign survey access for all users in this role',
            value: 'restricted_per_role',
          },
        ],
      },
      actions: [
        {
          name: 'manage_surveys',
          value: state.permissions['manage_surveys'],
          label: 'Manage surveys',
        },
      ],
    },
    {
      title: 'Feedback',
      view_permission: {
        name: 'view_feedback',
        value: state.permissions['view_feedback'],
        description:
          'To restrict feedback visibility, adjust the survey access in the Surveys section above. Feedback access is based on which surveys the user can access.',
        options: [
          {
            key: 'survey_access',
            text: 'Only feedback from surveys the user can access',
            value: 'survey_access',
          },
          {
            key: 'survey_access_team_member_mapping',
            text: 'Only feedback associated with the user',
            value: 'survey_access_team_member_mapping',
          },
        ],
      },
      actions: [
        {
          name: 'delete_responses',
          value: state.permissions['delete_responses'],
          label: 'Delete responses',
        },
        {
          name: 'edit_ratings_on_responses',
          value: state.permissions['edit_ratings_on_responses'],
          label: 'Edit ratings',
        },
        {
          name: 'edit_tags_on_responses',
          value: state.permissions['edit_tags_on_responses'],
          label: 'Edit tags',
        },
        {
          name: 'edit_team_members_on_responses',
          value: state.permissions['edit_team_members_on_responses'],
          label: 'Edit team members',
        },
        {
          name: 'edit_topics_on_responses',
          value: state.permissions['edit_topics_on_responses'],
          label: 'Edit topics',
        },
        {
          name: 'import_feedback',
          value: state.permissions['import_feedback'],
          label: 'Import feedback',
        },
        {
          name: 'publish_feedback',
          value: state.permissions['publish_feedback'],
          label: 'Publish feedback',
        },
        {
          name: 'manage_feedback_views',
          value: state.permissions['manage_feedback_views'],
          label: 'Manage views',
        },
        {
          name: 'manage_feedback_scheduled_reports',
          value: state.permissions['manage_feedback_scheduled_reports'],
          label: 'Manage scheduled reports',
        },
      ],
    },
    {
      title: 'Customers',
      view_permission: {
        name: 'view_customers',
        value: state.permissions['view_customers'],
        description:
          "Users with restricted feedback access won't be able to see hidden feedback while viewing customer data.",
        options: [
          {
            key: 'all',
            text: 'All customers',
            value: 'all',
          },
          {
            key: 'restricted_by_feedback_access',
            text: 'Only customers with responses the user can access',
            value: 'restricted_by_feedback_access',
          },
        ],
      },
      actions: [
        {
          name: 'manage_customers',
          label: 'Manage customers',
          value: state.permissions['manage_customers'],
        },
        {
          name: 'export_customers',
          label: 'Export customers',
          value: state.permissions['export_customers'],
        },
      ],
    },
    {
      title: 'Team members',
      view_permission: {
        name: 'view_team_members',
        value: state.permissions['view_team_members'] ?? 'no_access',
        description: '',
        options: [
          {
            key: 'all',
            text: 'All team members',
            value: 'all',
          },
          {
            key: 'no_access',
            text: 'No access',
            value: 'no_access',
          },
        ],
      },
      actions: [
        {
          name: 'manage_team_members',
          label: 'Manage team members',
          value: state.permissions['manage_team_members'],
        },
      ],
    },
    {
      title: 'Reports',
      view_permission: {
        name: 'view_reports',
        value: state.permissions['view_reports'],
        description: '',
        options: [
          {
            key: 'all',
            text: 'Only reports containing data the user can access',
            value: 'all',
          },
          {
            key: 'no_access',
            text: 'No access',
            value: 'no_access',
          },
        ],
      },
      actions: [
        {
          name: 'manage_reports_views',
          value: state.permissions['manage_reports_views'],
          label: 'Manage views',
        },
        {
          name: 'manage_reports_scheduled_reports',
          value: state.permissions['manage_reports_scheduled_reports'],
          label: 'Manage scheduled reports',
        },
      ],
    },
    {
      title: 'Survey dashboards',
      view_permission: {
        name: 'view_survey_dashboards',
        value: state.permissions['view_survey_dashboards'],
        description: '',
        options: [
          {
            key: 'all',
            text: 'Only dashboards from surveys the user can access',
            value: 'all',
          },
          {
            key: 'no_access',
            text: 'None',
            value: 'no_access',
          },
        ],
      },
      actions: [
        {
          name: 'manage_dashboard_views',
          value: state.permissions['manage_dashboard_views'],
          label: 'Manage views',
        },
      ],
    },
    {
      title: 'Custom dashboards',
      view_permission: {
        name: 'view_custom_dashboards',
        value: state.permissions['view_custom_dashboards'],
        description: '',
        options: [
          {
            key: 'all',
            text: 'All custom dashboards',
            value: 'all',
          },
          {
            key: 'restricted_per_account',
            text: 'Assign dashboards individually to users',
            value: 'restricted_per_account',
          },
          {
            key: 'restricted_per_role',
            text: 'Assign dashboard access for all users in this role',
            value: 'restricted_per_role',
          },
        ],
      },
      actions: [
        {
          name: 'manage_custom_dashboards',
          value: state.permissions['manage_custom_dashboards'],
          label: 'Manage custom dashboards',
        },
      ],
    },
    {
      title: 'Integrations',
      actions: [
        {
          name: 'manage_integrations',
          value: state.permissions['manage_integrations'],
          label: 'Manage integrations',
        },
      ],
    },
    {
      title: 'Settings',
      actions: [],
      action_groups: [
        {
          group: 'Workspace settings',
          actions: [
            {
              name: 'manage_account_settings',
              value: state.permissions['manage_account_settings'],
              label: 'Manage workspace settings',
            },
            {
              name: 'access_audit_logs',
              value: state.permissions['access_audit_logs'],
              label: 'Access audit logs',
            },
            {
              name: 'access_api_key',
              value: state.permissions['access_api_key'],
              label: 'Access API key',
            },
          ],
        },
        {
          group: 'Brands',
          actions: [
            {
              name: 'manage_brands',
              value: state.permissions['manage_brands'],
              label: 'Manage brands',
            },
          ],
        },
        {
          group: 'Topics',
          actions: [
            {
              name: 'manage_topic_settings',
              value: state.permissions['manage_topic_settings'],
              label: 'Manage topics',
            },
          ],
        },
        {
          group: 'Email authentication',
          actions: [
            {
              name: 'manage_email_authentication',
              value: state.permissions['manage_email_authentication'],
              label: 'Manage email authentication',
            },
          ],
        },
        {
          group: 'Feedback widgets',
          actions: [
            {
              name: 'manage_feedback_widgets',
              value: state.permissions['manage_feedback_widgets'],
              label: 'Manage feedback widgets',
            },
          ],
        },
        {
          group: 'Users',
          actions: [
            {
              name: 'manage_users',
              value: state.permissions['manage_users'],
              label: 'Manage users',
            },
            {
              name: 'manage_user_access_to_surveys',
              value: state.permissions['manage_user_access_to_surveys'],
              label: 'Manage user access to surveys',
            },
          ],
        },
        {
          group: 'Authentication security',
          actions: [
            {
              name: 'manage_authentication_security',
              value: state.permissions['manage_authentication_security'],
              label: 'Manage authentication security',
            },
          ],
        },
      ],
    },
    {
      title: 'Billing',
      view_permission: {
        name: 'view_billing_page',
        value: state.permissions['view_billing_page'],
        description: '',
        options: [
          {
            key: 'all',
            text: 'All billing information',
            value: 'all',
          },
          {
            key: 'no_access',
            text: 'No access',
            value: 'no_access',
          },
        ],
      },
      actions: [
        {
          name: 'manage_billing_information',
          value: state.permissions['manage_billing_information'],
          label: 'Manage billing information',
        },
      ],
    },
  ]
  function handlePermissionChange(paths, value) {
    onChange((prev) => R.assocPath(paths, value, prev))
  }

  function covertFirstLetterToUpperCase(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const roleName = state.is_predefined ? covertFirstLetterToUpperCase(state.name) : state.name
  return (
    <Form noValidate onSubmit={onSubmit} loading={loading}>
      <input type="submit" hidden ref={hiddenRefButton} />
      {!isDefaultRole && (
        <Form.Field>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://help.simplesat.io/en/articles/2361167-users-permissions#custom-roles"
          >
            <i className="icon far fa-book" />
            Learn more about custom roles
          </a>
        </Form.Field>
      )}
      <Form.Field error={Boolean(nameError)}>
        <label>Role name</label>
        <Input
          name="name"
          maxLength={200}
          value={roleName}
          disabled={isDisabled}
          onChange={(_, { value }) => handlePermissionChange(['name'], value)}
        />
        {nameError && <ErrorText>{nameError}</ErrorText>}
      </Form.Field>
      <Form.Field>
        <label>Description</label>
        <Input
          name="description"
          maxLength={300}
          value={state.description}
          placeholder="Summarize the role and key responsibilities (e.g., ‘Manages surveys and feedback’)."
          disabled={isDisabled}
          onChange={(_, { value }) => handlePermissionChange(['description'], value)}
        />
      </Form.Field>
      {isDefaultCollaborator && (
        <Form.Field>
          <Message
            compact
            header="Editing feedback access for Collaborators"
            content="For the default Collaborator role, you can customize only feedback visibility settings. All other permissions are fixed and cannot be modified."
          />
        </Form.Field>
      )}
      <Form.Field>
        <label>Permissions</label>
        <Accordion
          fluid
          styled
          exclusive={false}
          panels={permissions.map((permission) => {
            return {
              key: permission.title,
              title: { content: permission.title },
              content: {
                content: (
                  <React.Fragment>
                    {permission.view_permission && (
                      <VisibilityPermissionForm
                        isEnabledSurveyAccess={
                          permission.title === 'Surveys' &&
                          permission.view_permission.value === 'restricted_per_role'
                        }
                        setting={permission.view_permission}
                        handlePermissionChange={handlePermissionChange}
                        surveyAccesses={state.survey_access}
                        actions={permission.actions}
                        isEnabledDashboardAccess={
                          permission.title === 'Custom dashboards' &&
                          permission.view_permission.value === 'restricted_per_role'
                        }
                        dashbardAccesses={state.dashboard_access}
                        isFetchingSurveys={isFetchingSurveys}
                        isFetchingDashboards={isFetchingDashboards}
                        isDisabled={isDisabled}
                        isDefaultCollaborator={isDefaultCollaborator}
                      />
                    )}

                    <ActionPermissionForm
                      actions={permission.actions}
                      handlePermissionChange={handlePermissionChange}
                      isDisabled={permission.view_permission?.value === 'no_access' || isDisabled}
                    />

                    {permission.action_groups && (
                      <ActionGroupForm
                        actionGroups={permission.action_groups}
                        handlePermissionChange={handlePermissionChange}
                        isDisabled={isDisabled}
                      />
                    )}
                  </React.Fragment>
                ),
              },
            }
          })}
        />
      </Form.Field>
    </Form>
  )
}

interface CustomRoleFormProps {
  hiddenRefButton?: React.RefObject<HTMLInputElement>
  onChange: (state: any) => void
  state: Role
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  isFetchingSurveys?: boolean
  isFetchingDashboards?: boolean
  isDisabled?: boolean
  isDefaultRole?: boolean
  isDefaultCollaborator?: boolean
  loading?: boolean
  nameError?: string
}

export interface ActionGroupItem {
  name: string
  value: boolean | string
  parent_action?: string
  children_actions?: string[]
  label: string
}
export interface ActionGroup {
  group: string
  actions: ActionGroupItem[]
}

interface Permission {
  title: string
  view_permission?: {
    name: string
    value: boolean | string
    description: string
    options: {
      key: string
      text: string
      value: string
    }[]
  }
  actions: {
    name: string
    label: string
    value: boolean | string
  }[]
  action_groups?: ActionGroup[]
}

export interface Role {
  id: number | null
  name: string
  description: string
  is_predefined: boolean
  permissions: {
    [key: string]: boolean | string
  }
  survey_access: {
    token: string
    name: string
    has_permission: boolean
  }[]
  dashboard_access: {
    token: string
    name: string
    has_permission: boolean
  }[]
}



// WEBPACK FOOTER //
// ./src/components/CustomRoleForm/index.tsx